import {
  ACTIVE_STATES,
  DashboardFilters,
  type DashboardFilters as DashboardFiltersValue,
} from '@/components/opportunity/dashboard-filters';
import { ExecuteDialog } from '@/components/opportunity/execute-dialog';
import { OpportunityCard } from '@/components/opportunity/opportunity-card';
import { TrackedPickCard } from '@/components/opportunity/tracked-pick-card';
import { MetricsStrip } from '@/components/system/metrics-strip';
import { ShortcutsHelp } from '@/components/system/shortcuts-help';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { useDashboardShortcuts } from '@/hooks/use-dashboard-shortcuts';
import { useKeyboardShortcuts } from '@/hooks/use-keyboard-shortcuts';
import { trpc } from '@/lib/trpc';
import type { OpportunityRow } from '@tennis-scanner/shared';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

// Live operator dashboard — the page they stare at all day. Three sections:
//   1. KPIs (matches in radar, signals active, opps today, last alert)
//   2. "Alertas con value" — opportunities (book ≥ target). PRIMARY.
//   3. "Tus picks vigilados" — every active signal. Filtered to exclude
//      rows that already have an opportunity (= already in section 2).
//
// Polling: 5s on opportunities.list + signals.tracked. Both endpoints embed
// match metadata inline (player names, tournament, start time) so the cards
// don't need a separate matches.search round-trip.
//
// Keyboard shortcuts (operator throughput, see use-dashboard-shortcuts):
//   j/k       — next/prev opportunity (the focus ring lands on the alert)
//   e/p/s     — execute / descartar / pausar the focused alert
//   Enter     — open match cockpit
//   ?         — show help

const DEFAULT_FILTERS: DashboardFiltersValue = {
  states: ACTIVE_STATES,
  sortBy: 'rankScore',
};

export function DashboardPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [filters, setFilters] = useState<DashboardFiltersValue>(DEFAULT_FILTERS);
  const [executing, setExecuting] = useState<OpportunityRow | null>(null);
  const [focusIdx, setFocusIdx] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [limit, setLimit] = useState(50);
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);

  // staleTime matches refetchInterval so background polling doesn't flip
  // isLoading=true → skeletons would flash on every cycle otherwise.
  const opps = trpc.opportunities.list.useQuery(
    { state: filters.states, sortBy: filters.sortBy, limit },
    { refetchInterval: 5000, refetchIntervalInBackground: false, staleTime: 5000 },
  );

  const tracked = trpc.signals.tracked.useQuery(
    { includeInactive: false, limit: 100 },
    { refetchInterval: 5000, refetchIntervalInBackground: false, staleTime: 5000 },
  );

  const rows = opps.data ?? [];

  // Tracked picks split into 3 groups so the operator never has to scan
  // both sections for value:
  //   1. trackedHasOpp     → already promoted to opportunity; hidden here
  //                          (it's already in the `rows` list above).
  //   2. trackedWithValue  → at least one allowed book pays ≥ target
  //                          (bestEdgePct ≥ 0). Promotion to `opportunities`
  //                          may lag the next scanner cycle — surface it
  //                          NOW so the operator can act.
  //   3. trackedNoValue    → no value yet. Goes into the "sin value todavía"
  //                          section.
  const { trackedWithValue, trackedNoValue } = useMemo(() => {
    const withValue = [];
    const noValue = [];
    for (const t of tracked.data ?? []) {
      if (t.opportunityId !== null) continue; // already an opp
      if (t.bestEdgePct !== null && t.bestEdgePct >= 0) withValue.push(t);
      else noValue.push(t);
    }
    return { trackedWithValue: withValue, trackedNoValue: noValue };
  }, [tracked.data]);

  const snooze = trpc.opportunities.snooze.useMutation({
    onSuccess: async () => {
      toast.success('Alerta pausada 1h');
      await utils.opportunities.list.invalidate();
    },
    onError: (err) => toast.error('No se pudo pausar', { description: err.message }),
  });
  const markPassed = trpc.opportunities.markPassed.useMutation({
    onSuccess: async () => {
      toast.success('Alerta descartada');
      await utils.opportunities.list.invalidate();
    },
    onError: (err) => toast.error('No se pudo descartar', { description: err.message }),
  });

  const focused = rows[Math.min(focusIdx, rows.length - 1)] ?? null;
  const scrollFocusIntoView = useCallback((idx: number): void => {
    const el = cardRefs.current[idx];
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, []);

  const shortcuts = useDashboardShortcuts({
    rows,
    focused,
    setFocusIdx,
    setExecuting,
    setHelpOpen,
    scrollFocusIntoView,
    navigate,
    snooze,
    markPassed,
  });
  useKeyboardShortcuts(shortcuts);

  return (
    <div className="space-y-4">
      <header className="flex items-baseline justify-between">
        <h1
          className="font-brand text-3xl tracking-[0.25em] text-foreground"
          data-testid="page-title"
        >
          ALERTAS EN VIVO
        </h1>
        <p className="text-xs text-muted-foreground" data-testid="page-description">
          Refrescando cada 5s · presioná <kbd className="font-mono">?</kbd> para atajos
        </p>
      </header>

      <MetricsStrip />

      {/* Section 1: Opportunities (the value-positive picks). PRIMARY. */}
      <section className="space-y-2 pt-2">
        <header className="flex items-baseline justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-positive">
            Alertas con value
          </h2>
          <span className="font-mono text-xs text-muted-foreground">
            {rows.length + trackedWithValue.length}{' '}
            {rows.length + trackedWithValue.length === 1 ? 'alerta' : 'alertas'}
          </span>
        </header>
        <DashboardFilters filters={filters} onChange={setFilters} totalCount={rows.length} />

        {opps.isError && (
          <Alert variant="destructive">
            <AlertTitle>Error cargando alertas</AlertTitle>
            <AlertDescription>
              {opps.error.message} — el sistema reintentará automáticamente.
            </AlertDescription>
          </Alert>
        )}

        {/* Skeletons only on first load (no cached data yet). Background
            refetches keep `opps.data` populated so we don't flash skeletons. */}
        {!opps.data && (
          <div className="grid gap-1.5">
            <Skeleton className="h-32" />
            <Skeleton className="h-32" />
          </div>
        )}

        {opps.data && rows.length === 0 && trackedWithValue.length === 0 && (
          <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border bg-card py-8 text-center">
            <p className="text-sm font-medium text-foreground">No hay alertas con value todavía</p>
            <p className="max-w-md text-xs text-muted-foreground">
              Una alerta aparece acá cuando alguna casa paga ≥ tu target. Mientras tanto, mirá los
              picks abajo y su distancia al target.
            </p>
          </div>
        )}

        <div className="grid gap-1.5">
          {rows.map((opp, idx) => (
            <div
              key={opp.id}
              ref={(el) => {
                cardRefs.current[idx] = el;
              }}
              className={
                idx === focusIdx
                  ? 'rounded-lg ring-2 ring-primary ring-offset-2 ring-offset-background'
                  : ''
              }
            >
              <OpportunityCard
                opportunity={opp}
                onExecuteClick={setExecuting}
                onSelect={(o) => {
                  setFocusIdx(idx);
                  navigate(`/matches/${o.canonicalMatchId}`);
                }}
              />
            </div>
          ))}
          {/* Tracked picks que ya tienen al menos una casa verde pero el
              scanner-worker todavía no las promovió a opportunity. Aparecen
              acá para que el operador no espere el próximo ciclo. */}
          {trackedWithValue.map((t) => (
            <TrackedPickCard key={t.signal.id} tracked={t} />
          ))}
        </div>

        {rows.length >= limit && (
          <div className="flex justify-center pt-2">
            <button
              type="button"
              onClick={() => setLimit((l) => Math.min(l + 50, 500))}
              className="rounded-md border border-border bg-card px-4 py-1.5 text-sm font-medium text-muted-foreground hover:bg-popover hover:text-foreground"
            >
              Mostrar 50 más (límite máx 500)
            </button>
          </div>
        )}
      </section>

      {/* Section 2: Tracked picks waiting for value. Hidden when empty. */}
      {trackedNoValue.length > 0 && (
        <section className="space-y-2 pt-4">
          <header className="flex items-baseline justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
              Tus picks vigilados (sin value todavía)
            </h2>
            <span className="font-mono text-xs text-muted-foreground">
              {trackedNoValue.length} {trackedNoValue.length === 1 ? 'pick' : 'picks'}
            </span>
          </header>
          <div className="grid gap-1.5">
            {trackedNoValue.map((t) => (
              <TrackedPickCard key={t.signal.id} tracked={t} />
            ))}
          </div>
        </section>
      )}

      <ExecuteDialog opportunity={executing} onClose={() => setExecuting(null)} />
      <ShortcutsHelp open={helpOpen} onClose={() => setHelpOpen(false)} shortcuts={shortcuts} />
    </div>
  );
}
